import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { fantasy } from '@sorare/core/src/lib/glossary';
import {
  breakpoints,
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { useEvents } from 'hooks/useEvents';

import { BackLink } from './BackLink';

const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  position: relative;
  isolation: isolate;
`;

const ResponsiveLayout = styled.div<{ fullWidth?: boolean }>`
  width: 100%;
  display: grid;
  grid-auto-rows: max-content;
  grid-template-areas: 'body' 'side';
  gap: var(--quadruple-unit);
  padding-bottom: var(--triple-unit);
  align-self: center;
  max-width: calc(${breakpoints.desktop}px + var(--quadruple-unit));
  @media ${laptopAndAbove} {
    grid-template-columns: 6fr 1fr 5fr;
    grid-template-areas: 'body none side';
    gap: 0;
    padding-bottom: var(--quadruple-unit);
  }
  ${({ fullWidth }) =>
    fullWidth &&
    css`
      grid-template-areas: 'body';
      @media ${laptopAndAbove} {
        grid-template-columns: 1fr;
        grid-template-areas: 'body';
      }
    `}
`;

const Spacer = styled.div`
  @media ${laptopAndAbove} {
    margin-top: var(--quadruple-unit);
  }
`;

const StyledButton = styled(Button)`
  flex: 1;
`;

const Stack = styled.div<{ area: 'body' | 'side' }>`
  grid-area: ${({ area }) => area};
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  overflow: hidden;
`;

const DesktopBackLinkTitle = styled.h1`
  font: var(--t-bold) var(--t-20);
  display: none;
  @media ${laptopAndAbove} {
    display: inline;
  }
`;

const MobileFixedBottom = styled.div`
  flex: none;
  display: flex;
  padding: var(--intermediate-unit) var(--double-unit);
  touch-action: none;
  -webkit-user-drag: none;
  user-select: none;

  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  width: 100vw;
  border-radius: var(--double-unit) var(--double-unit) 0 0;
  box-shadow: 0 0 var(--double-unit) 1px rgba(0, 0, 0, 0.5);
  color: var(--c-neutral-1000);
  isolation: isolate;
  background: rgba(var(--c-rgb-neutral-100), 0.7);
  backdrop-filter: blur(var(--half-unit));
  border: 0.5px solid var(--c-neutral-300);
  @media ${tabletAndAbove} {
    display: none;
  }
`;

type Props = {
  children: ReactNode;
  header?: ReactNode;
  backLink?: ReactNode;
  featured?: ReactNode;
  leaderboard?: ReactNode;
  registerPath?: string | false;
  showingLineup?: boolean;
  fullWidth?: boolean;
};

export const Layout = ({
  children,
  header,
  backLink,
  featured,
  leaderboard,
  registerPath,
  showingLineup = false,
  fullWidth = false,
}: Props) => {
  const track = useEvents();
  return (
    <Wrapper>
      {header}
      {backLink && <BackLink>{backLink}</BackLink>}
      <ResponsiveLayout fullWidth={fullWidth}>
        <Stack area="body">
          {backLink && (
            <Container>
              <DesktopBackLinkTitle>{backLink}</DesktopBackLinkTitle>
            </Container>
          )}
          {children}
          {featured && <Container>{featured}</Container>}
        </Stack>
        {leaderboard && (
          <Stack area="side">
            <ConditionalWrapper Wrapper={Spacer} wrap={showingLineup}>
              <Container>{leaderboard}</Container>
            </ConditionalWrapper>
          </Stack>
        )}
      </ResponsiveLayout>
      {registerPath && (
        <MobileFixedBottom>
          <StyledButton
            size="medium"
            color="primary"
            to={registerPath}
            onClick={() => track('Click Compose Lineup')}
          >
            <FormattedMessage {...fantasy.registerLineup} />
          </StyledButton>
        </MobileFixedBottom>
      )}
    </Wrapper>
  );
};
